import React, { useState } from 'react';
import axios from 'axios';
import { Alert, Button, Column, List, Row, Select, TextArea, TextInput } from '@pella/sash-framework';
import { SUBMIT_WEB_CONTACT_FORM } from '../../../../enums/graphql';
import { isValidArray, isValidEmail } from '../../../utilities/validations';
import { useMutation } from '@apollo/client';

const ContactUs = () => {
	const [values, setValues] = useState({ email: '', help: '', productType: '', category: '', orderNumber: '', poNumber: '', serialNumber: '', files: '' });
	const [errors, setErrors] = useState({});
	const [status, setStatus] = useState('idle');
	const [addedFiles, setAddedFiles] = useState([]);
	const [submitForm] = useMutation(SUBMIT_WEB_CONTACT_FORM, { onCompleted: () => onFormSuccess(), onError: (error) => onFormError(error) });

	const errorMessages = {
		email: 'Valid Email Address is required',
		help: 'This field is required',
		productType: 'Please select a product type',
		category: 'Please select a category',
		attachments: 'Error uploading attachments, please try again',
		fileSize: 'Total file size can not exceed 10mb',
		fileAmount: 'You can only upload up to 12 files',
	};

	const onInputChange = (event) => {
		const { name, type, value } = event.target;

		let savedValue = value;
		if (type === 'file') {
			savedValue = event.target.files;
			const allFiles = [...addedFiles, ...savedValue];

			if (!validateTotalSize(allFiles)) {
				setErrors({ ...errors, attachments: errorMessages.fileSize });
				return;
			}

			if (allFiles.length > 12) {
				setErrors({ ...errors, attachments: errorMessages.fileAmount });
				return;
			}

			setAddedFiles(allFiles);
		}

		setValues({ ...values, [name]: savedValue });
		if (name !== 'email') {
			validateField(name, savedValue);
		}
	};

	const onInputBlur = (event) => {
		const { name, value } = event.target;
		if (name === 'email') {
			validateField(name, value);
		}
	};

	const validateField = (name, value) => {
		let error = '';

		if (name === 'email') {
			if (!value || (value && !isValidEmail(value))) {
				error = errorMessages.email;
			}
		} else if (name === 'help' && !value) {
			error = errorMessages.help;
		} else if (name === 'productType' && !value) {
			error = errorMessages.productType;
		} else if (name === 'category' && !value) {
			error = errorMessages.category;
		}

		setErrors({ ...errors, [name]: error });
	};

	const validateForm = () => {
		const errors = {};

		if (!values.email || (values.email && !isValidEmail(values.email))) errors.email = errorMessages.email;
		if (!values.help) errors.help = errorMessages.help;
		if (!values.productType) errors.productType = errorMessages.productType;
		if (!values.category) errors.category = errorMessages.category;

		setErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const onFormSubmit = async (event) => {
		event.preventDefault();
		setStatus('processing');
		setErrors({});

		if (!validateForm()) {
			window.scrollTo({ top: 0, behavior: 'smooth' });
			setStatus('error');
			return;
		}

		// upload files
		let attachmentIds = [];
		if (isValidArray(addedFiles)) {
			const fileData = new FormData();
			addedFiles.forEach((file) => fileData.append('photos', file));

			try {
				const attachments = await axios.post(`${process.env.GATSBY_AWS_API_URL}/service-api/public/upload`, fileData);
				attachmentIds = attachments.data;
			} catch (error) {
				setErrors({ ...errors, attachments: errorMessages.attachments, message: 'There was an error trying to upload the files' });
				setStatus('error');
			}

			if (!isValidArray(attachmentIds)) {
				window.scrollTo({ top: 0, behavior: 'smooth' });
				return;
			}
		}

		const input = {
			data: {
				category: values.category,
				comments: values.help,
				email: values.email,
				productType: values.productType,
				...(values.orderNumber && { orderNumber: values.orderNumber }),
				...(values.poNumber && { poNumber: values.poNumber }),
				...(values.serialNumber && { serialNumber: values.serialNumber }),
				...(isValidArray(attachmentIds) && { attachments: attachmentIds }),
			},
		};

		await submitForm({ variables: { input } });
	};

	const onFormSuccess = () => {
		setValues({ email: '', help: '', productType: '', category: '', orderNumber: '', poNumber: '', serialNumber: '', files: '' });
		setAddedFiles([]);
		window.scrollTo({ top: 0, behavior: 'smooth' });
		setStatus('success');
	};

	const onFormError = (error) => {
		setErrors({ ...errors, message: error.message });
		setStatus('error');
	};

	const validateTotalSize = (files) => {
		let totalSize = 0;
		files.forEach((file) => (totalSize += file.size));

		return totalSize / 1024 / 1024 < 10;
	};

	const onRemoveClick = (fileIndex) => {
		const newFiles = [...addedFiles].filter((_, index) => index !== fileIndex);
		setAddedFiles(newFiles);

		if (!validateTotalSize(newFiles)) {
			setErrors({ ...errors, attachments: errorMessages.fileSize });
			return;
		}

		if (errors.attachments) {
			setErrors((current) => ({ ...current, attachments: '' }));
		}
	};

	return (
		<div
			className='contact-web-form mt-6'
			data-testid='contact-web-form'
		>
			{status === 'success' && (
				<Alert
					type='success'
					className='mb-4'
					hideClose
				>
					The form has been submitted successfully!
				</Alert>
			)}

			{errors.message && (
				<Alert
					type='error'
					className='mb-4'
					hideClose
				>
					{errors.message}
				</Alert>
			)}

			<form
				onSubmit={onFormSubmit}
				noValidate
			>
				<Row>
					<Column xs={12}>
						<TextInput
							type='email'
							id='email'
							labelText='Email Address'
							onChange={onInputChange}
							onBlur={onInputBlur}
							error={errors.email ? true : false}
							errorText={errors.email}
							autoComplete='email'
							value={values.email}
							required
						/>
					</Column>

					<Column xs={12}>
						<TextArea
							id='help'
							labelText='How can we help?'
							onChange={onInputChange}
							error={errors.help ? true : false}
							errorText={errors.help}
							value={values.help}
							required
						/>
					</Column>

					<Column
						xs={12}
						md={6}
					>
						<Select
							id='productType'
							labelText='What type of product do you have a question about?'
							onChange={onInputChange}
							error={errors.productType ? true : false}
							errorText={errors.productType}
							value={values.productType}
							required
						>
							<option value=''>Select a product type</option>
							<option value='Windows'>Windows</option>
							<option value='Entry Doors'>Entry Doors</option>
							<option value='Patio Doors'>Patio Doors</option>
							<option value='Garage Doors'>Garage Doors</option>
							<option value='Storm Doors'>Storm Doors</option>
						</Select>
					</Column>

					<Column
						xs={12}
						md={6}
					>
						<Select
							id='category'
							labelText='What type of category do you have a question about?'
							onChange={onInputChange}
							error={errors.category ? true : false}
							errorText={errors.category}
							value={values.category}
							required
						>
							<option value=''>Select a category</option>
							<option value='Cleaning and Maintenance'>Cleaning and Maintenance</option>
							<option value='Installation'>Installation</option>
							<option value='Replacement Parts/Troubleshooting'>Replacement Parts/Troubleshooting</option>
							<option value='Operation'>Operation</option>
							<option value='Finishing'>Finishing</option>
							<option value='Energy and Performance'>Energy and Performance</option>
							<option value='Warranty Information'>Warranty Information</option>
							<option value='Product Information and Brochures'>Product Information and Brochures</option>
						</Select>
					</Column>

					<Column
						xs={12}
						md={6}
					>
						<TextInput
							id='orderNumber'
							labelText='Order Number'
							onChange={onInputChange}
							helpText='Where can I find this?'
							tooltipContent='Please include the order number found on the receipt received at the time of purchase.'
							value={values.orderNumber}
						/>
					</Column>

					<Column
						xs={12}
						md={6}
					>
						<TextInput
							id='poNumber'
							labelText='PO Number'
							onChange={onInputChange}
							helpText='Where can I find this?'
							tooltipContent='Please include the PO number found on the receipt received at the time of purchase.'
							value={values.poNumber}
						/>
					</Column>

					<Column
						xs={12}
						md={6}
					>
						<TextInput
							id='serialNumber'
							labelText='Serial Number'
							onChange={onInputChange}
							helpText='Where can I find this?'
							tooltipContent={
								<p>
									Please enter serial number of the product you are contacting us about. If you have trouble locating the serial number please review the following <a href='/support-center/serial-number/'>answer</a>.
								</p>
							}
							value={values.serialNumber}
						/>
					</Column>

					<Column
						xs={12}
						md={6}
					>
						<TextInput
							type='file'
							id='attachments'
							labelText='Attach Documents'
							onChange={onInputChange}
							value={values.files}
							accept='image/*,video/*,audio/*,.txt,.pdf,.docx,.doc,.csv,.xlsx,.xls,.odt,.odm,.ods,.odp,.odf,.msg,.eml,.rtf,.ppt,.pptx,.htm,.html,.zip'
							disabled={addedFiles.length >= 12}
							error={errors?.attachments ? true : false}
							errorText={errors?.attachments}
							multiple
						/>

						{isValidArray(addedFiles) && (
							<List bullets='none'>
								{addedFiles.map((file, index) => (
									<li
										key={`file-${index}`}
										className='flex items-center gap-x-2'
									>
										<span className='truncate'>{file.name}</span>
										<button
											type='button'
											className='text-blue'
											onClick={() => onRemoveClick(index)}
										>
											Remove
										</button>
									</li>
								))}
							</List>
						)}
					</Column>

					<Column xs={12}>
						<Button
							color='primary-alt'
							type='submit'
							disabled={status === 'processing'}
						>
							{status === 'processing' ? 'Processing...' : 'Submit Your Question'}
						</Button>
					</Column>

					<Column
						xs={12}
						className='mt-5'
					>
						<p>Your privacy is important to us. The information you submit here will be retained and used only as is necessary to respond to your request and to keep a record of the request.</p>
					</Column>
				</Row>
			</form>
		</div>
	);
};

export default ContactUs;
